import { useState, useMemo } from 'react';
import { customInstance, useGetUsers } from '@edison/api-client-react';
import { AlertCircle, RefreshCw, User, Calendar, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

interface TimeEntry {
  id: string;
  projectId: string;
  projectName?: string | null;
  userId: string;
  startTime: string;
  endTime: string;
  rdPercentage?: string | null;
  reviewStatus: string;
  source?: string | null;
  metadata?: { developerNames?: string[]; developerEmails?: string[] } | null;
  userName?: string | null;
  userEmail?: string | null;
}

interface UserRecord {
  id: string;
  name?: string | null;
  email: string;
  active?: boolean | null;
}

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatDuration(startTime: string, endTime: string): string {
  const ms = new Date(endTime).getTime() - new Date(startTime).getTime();
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

function formatHours(ms: number): string {
  return new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 }).format(ms / 3_600_000);
}

// Per-user interval union deduplication — avoids double-counting CC+GitHub overlaps
function computeUnionHoursMs(entries: TimeEntry[]): number {
  const byUser = new Map<string, Array<{ start: number; end: number }>>();
  for (const e of entries) {
    const start = new Date(e.startTime).getTime();
    const end = new Date(e.endTime).getTime();
    if (!byUser.has(e.userId)) byUser.set(e.userId, []);
    byUser.get(e.userId)!.push({ start, end });
  }
  let totalMs = 0;
  for (const intervals of byUser.values()) {
    intervals.sort((a, b) => a.start - b.start);
    let cur: { start: number; end: number } | null = null;
    for (const iv of intervals) {
      if (!cur || iv.start > cur.end) {
        if (cur) totalMs += cur.end - cur.start;
        cur = { ...iv };
      } else {
        cur.end = Math.max(cur.end, iv.end);
      }
    }
    if (cur) totalMs += cur.end - cur.start;
  }
  return totalMs;
}

function computeRdHoursMs(entries: TimeEntry[]): number {
  // Same interval union but weight by max rdPercentage on overlap
  const byUser = new Map<string, Array<{ start: number; end: number; pct: number }>>() ;
  for (const e of entries) {
    const start = new Date(e.startTime).getTime();
    const end = new Date(e.endTime).getTime();
    const pct = e.rdPercentage != null ? parseFloat(e.rdPercentage) : 0;
    if (!byUser.has(e.userId)) byUser.set(e.userId, []);
    byUser.get(e.userId)!.push({ start, end, pct });
  }
  let totalMs = 0;
  for (const intervals of byUser.values()) {
    intervals.sort((a, b) => a.start - b.start);
    let cur: { start: number; end: number; pct: number } | null = null;
    for (const iv of intervals) {
      if (!cur || iv.start > cur.end) {
        if (cur) totalMs += (cur.end - cur.start) * (cur.pct / 100);
        cur = { ...iv };
      } else {
        cur.end = Math.max(cur.end, iv.end);
        cur.pct = Math.max(cur.pct, iv.pct);
      }
    }
    if (cur) totalMs += (cur.end - cur.start) * (cur.pct / 100);
  }
  return totalMs;
}

// Default date range: last 30 days
function defaultDateFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

function defaultDateTo(): string {
  return new Date().toISOString().slice(0, 10);
}

// ────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────

export function TimesheetView() {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState(defaultDateFrom());
  const [dateTo, setDateTo] = useState(defaultDateTo());
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const { data: usersData } = useGetUsers();
  const users: UserRecord[] = (usersData?.data?.items ?? []) as unknown as UserRecord[];

  async function fetchEntries() {
    if (!selectedUserId) return;
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const params = new URLSearchParams({ userId: selectedUserId, dateFrom, dateTo });
      const res = await customInstance<{ data: { items: TimeEntry[] } }>({
        url: `/api/time-entries?${params.toString()}`,
        method: 'GET',
      });
      setEntries(res.data.items ?? []);
    } catch {
      setError('Failed to load time entries. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // Group entries by calendar date (local AU)
  const byDate = useMemo(() => {
    const grouped = new Map<string, TimeEntry[]>();
    for (const e of entries) {
      const d = new Date(e.startTime).toLocaleDateString('en-AU', { year: 'numeric', month: '2-digit', day: '2-digit' });
      (grouped.get(d) ?? grouped.set(d, []).get(d))!.push(e);
    }
    // Sort dates descending
    return [...grouped.entries()].sort((a, b) => {
      // a[0] and b[0] are "DD/MM/YYYY" — convert for comparison
      const parse = (s: string) => { const [d, m, y] = s.split('/'); return `${y}-${m}-${d}`; };
      return parse(b[0]).localeCompare(parse(a[0]));
    });
  }, [entries]);

  const totalMs = computeUnionHoursMs(entries);
  const rdMs = computeRdHoursMs(entries);

  // Group entries by project for the summary panel
  const byProject = useMemo(() => {
    const grouped = new Map<string, { projectName: string; ms: number; rdMs: number }>();
    for (const e of entries) {
      if (e.reviewStatus !== 'approved') continue;
      const key = e.projectId;
      const name = e.projectName ?? e.projectId;
      if (!grouped.has(key)) grouped.set(key, { projectName: name, ms: 0, rdMs: 0 });
      const rec = grouped.get(key)!;
      const dur = new Date(e.endTime).getTime() - new Date(e.startTime).getTime();
      const pct = e.rdPercentage != null ? parseFloat(e.rdPercentage) : 0;
      rec.ms += dur;
      rec.rdMs += dur * (pct / 100);
    }
    return [...grouped.values()].sort((a, b) => b.ms - a.ms);
  }, [entries]);

  const selectedUser = users.find((u) => u.id === selectedUserId);

  return (
    <div className="space-y-6">
      {/* ── Filter bar ── */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-lg border bg-muted/20">
        {/* Person */}
        <div className="flex flex-col gap-1.5 min-w-52">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <User className="h-3 w-3" aria-hidden="true" />
            Person
          </label>
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="Select person"
          >
            <option value="">Select a person…</option>
            {users
              .filter((u) => u.active !== false)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name ?? u.email}
                </option>
              ))}
          </select>
        </div>

        {/* Date from */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Calendar className="h-3 w-3" aria-hidden="true" />
            From
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="Date from"
          />
        </div>

        {/* Date to */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Calendar className="h-3 w-3" aria-hidden="true" />
            To
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="Date to"
          />
        </div>

        <Button
          onClick={() => void fetchEntries()}
          disabled={!selectedUserId || loading}
        >
          {loading ? 'Loading…' : 'Show timesheet'}
        </Button>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
          <Button variant="secondary" size="sm" onClick={() => void fetchEntries()}>
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </Button>
        </div>
      )}

      {/* ── Empty state ── */}
      {searched && !loading && !error && entries.length === 0 && (
        <div className="text-center py-12 rounded-lg border border-dashed">
          <Clock className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" aria-hidden />
          <p className="text-sm text-muted-foreground">
            No time entries found for {selectedUser?.name ?? selectedUser?.email ?? 'this person'} in the selected period.
          </p>
        </div>
      )}

      {/* ── Results ── */}
      {!loading && entries.length > 0 && (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-lg border bg-card px-4 py-3">
              <p className="text-xs text-muted-foreground mb-1">Total hours (deduplicated)</p>
              <p className="text-2xl font-semibold tabular-nums">{formatHours(totalMs)}h</p>
            </div>
            <div className="rounded-lg border bg-card px-4 py-3">
              <p className="text-xs text-muted-foreground mb-1">R&D hours</p>
              <p className="text-2xl font-semibold tabular-nums">{formatHours(rdMs)}h</p>
            </div>
            <div className="rounded-lg border bg-card px-4 py-3">
              <p className="text-xs text-muted-foreground mb-1">R&D %</p>
              <p className="text-2xl font-semibold tabular-nums">
                {totalMs > 0 ? `${((rdMs / totalMs) * 100).toFixed(1)}%` : '—'}
              </p>
            </div>
          </div>

          {/* Project breakdown */}
          {byProject.length > 0 && (
            <div className="rounded-lg border bg-card p-4">
              <p className="text-sm font-medium mb-3">Hours by project (approved entries)</p>
              <div className="space-y-2">
                {byProject.map((p) => (
                  <div key={p.projectName} className="flex items-center justify-between text-sm">
                    <span className="truncate text-muted-foreground max-w-xs">{p.projectName}</span>
                    <div className="flex items-center gap-4 shrink-0 tabular-nums">
                      <span className="text-xs text-muted-foreground">{formatHours(p.ms)}h total</span>
                      <span className="font-medium">
                        {formatHours(p.rdMs)}h R&D
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Day-by-day breakdown */}
          <div className="space-y-6">
            {byDate.map(([dateKey, dayEntries]) => {
              const dayMs = computeUnionHoursMs(dayEntries);
              const dayRdMs = computeRdHoursMs(dayEntries);

              return (
                <div key={dateKey}>
                  <div className="flex items-baseline gap-3 mb-2 pb-1 border-b">
                    <span className="font-medium text-sm">{dayEntries[0] ? formatDate(dayEntries[0].startTime) : dateKey}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {formatHours(dayMs)}h
                      {dayRdMs > 0 && ` · ${formatHours(dayRdMs)}h R&D`}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {dayEntries
                      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
                      .map((entry) => {
                        const rdPct = entry.rdPercentage != null ? parseFloat(entry.rdPercentage) : null;
                        return (
                          <div
                            key={entry.id}
                            className="flex items-center justify-between rounded-md border bg-card/60 px-3 py-2 text-sm"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="font-mono text-xs text-muted-foreground tabular-nums shrink-0">
                                {formatTime(entry.startTime)}–{formatTime(entry.endTime)}
                              </span>
                              <span className="text-xs text-muted-foreground shrink-0">
                                {formatDuration(entry.startTime, entry.endTime)}
                              </span>
                              {entry.projectName && (
                                <span className="truncate text-xs">{entry.projectName}</span>
                              )}
                              {entry.source === 'claude_code' && (
                                <span className="text-xs text-muted-foreground shrink-0">CC</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {rdPct != null && rdPct > 0 && (
                                <span className="text-xs tabular-nums font-medium">
                                  {rdPct.toFixed(0)}% R&D
                                </span>
                              )}
                              {entry.reviewStatus === 'approved' ? (
                                <Badge variant="positive" className="text-xs">Approved</Badge>
                              ) : entry.reviewStatus === 'rejected' ? (
                                <Badge variant="destructive" className="text-xs">Rejected</Badge>
                              ) : entry.reviewStatus === 'classifying' ? (
                                <Badge variant="info" className="text-xs">Classifying</Badge>
                              ) : (
                                <Badge variant="warning" className="text-xs">Pending</Badge>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
